/**
 * หน้าร่วมบุญแบบรอบ (/merit · 27 ต.ค. 2569) — ตรรกะล้วน (ไม่แตะ DOM/เครือข่าย) ให้ทดสอบได้
 * ข้อมูลมาจาก CF donationSubmit actions: meritRounds · meritProgram · meritCreate (repo admin · donation/donationMerit.js)
 * ชื่อฟิลด์ต้องตรงกับฝั่ง CF
 */

export type MeritState = 'open' | 'closed' | 'soon' | 'hidden'
export type MeritTheme = 'gold' | 'velvet' | 'linen'
export type MeritPayments = 'beam' | 'manual_transfer' | 'both'
export type MeritBlock = { t: 'text'; h: string; v: string } | { t: 'img'; u: string; c: string }
export type MeritFormula = { unitPrice: number; unitLabel: string; remainderLabel: string }
export type MeritStats = { totalAmount: number; donorCount: number; pieces: number; remainder?: number }
export type MeritDonor = { name: string; amount: number; pieces: number; at: string }
export type MeritRound = { id: string; name: string; eventDate: string; state: MeritState; theme: MeritTheme }
export type MeritProgram = {
  id: string
  state: MeritState
  name?: string
  eventDate?: string
  description?: string
  motto?: string
  images?: string[]
  blocks?: MeritBlock[]
  theme?: MeritTheme
  formula?: MeritFormula | null
  payments?: MeritPayments
  openAt?: string
  closeAt?: string
  closedMessage?: string
  cfType?: string
  stats?: MeritStats | null
  showNames?: boolean
  donors?: MeritDonor[]
}

export const MERIT_THEMES: MeritTheme[] = ['gold', 'velvet', 'linen']

export function parseTheme(v: unknown): MeritTheme {
  return MERIT_THEMES.includes(v as MeritTheme) ? (v as MeritTheme) : 'gold'
}

/**
 * null = ไม่ใช่หน้านี้ · programId '' = เปิดรอบล่าสุด
 * ลิงก์: /merit · /merit/ · /donate (ลิงก์สั้นแชร์) หรือ ?view=merit[&p=รหัสรอบ] (รวมที่ห่อมาใน liff.state)
 */
export function readMeritParams(search: string, pathname = ''): { programId: string } | null {
  try {
    const sp = new URLSearchParams(search)
    const clean = (v: string | null) => (v && /^[A-Za-z0-9_-]{1,64}$/.test(v.trim()) ? v.trim() : '')
    if (/^\/(?:merit|donate)\/?$/i.test(pathname)) return { programId: clean(sp.get('p')) }
    let state = sp.get('liff.state')
    if (state && /%3[fd]/i.test(state)) state = decodeURIComponent(state)
    if (state?.startsWith('?')) state = state.slice(1)
    const st = new URLSearchParams(state || '')
    const src = sp.get('view') === 'merit' ? sp : st.get('view') === 'merit' ? st : null
    return src ? { programId: clean(src.get('p')) } : null
  } catch {
    return null
  }
}

/** ผืน/เศษ ตามสูตรของรอบ · ไม่มีสูตร = ไม่แสดงผืน */
export function piecesOf(amount: number, formula: MeritFormula | null | undefined): { pieces: number; remainder: number; toNext: number } {
  const a = Number.isFinite(amount) && amount > 0 ? amount : 0
  if (!formula || !formula.unitPrice || !a) return { pieces: 0, remainder: a, toNext: 0 }
  const pieces = Math.floor(a / formula.unitPrice)
  const remainder = Math.round((a - pieces * formula.unitPrice) * 100) / 100
  return { pieces, remainder, toNext: remainder > 0 ? Math.round((formula.unitPrice - remainder) * 100) / 100 : 0 }
}

/** ปุ่มลัดเป็นจำนวนผืน (ไม่มีสูตร = ไม่มีปุ่ม) */
export function quickChoices(formula: MeritFormula | null | undefined): { n: number; amount: number }[] {
  if (!formula || !formula.unitPrice) return []
  return [1, 2, 3, 5, 10].map((n) => ({ n, amount: n * formula.unitPrice }))
}

export function digitsAmount(raw: string): number {
  const n = parseInt(String(raw || '').replace(/\D/g, '').slice(0, 7), 10)
  return Number.isFinite(n) ? n : 0
}

export function cleanName(v: string): string {
  return String(v || '').replace(/\s+/g, ' ').trim().slice(0, 80)
}

/** ผู้ร่วมบุญต่อผืน (27 ต.ค. 2569) — mode one = ชื่อเดียวทุกผืน · each = แยกชื่อ 1 คน/ผืน */
export type MeritPerson = { name: string; birthday: string }
export type NameMode = 'one' | 'each'
export const MAX_PERSON_ROWS = 50

/** จำนวนแถวชื่อที่ต้องกรอก: ≤1 ผืน หรือโหมดชื่อเดียว = 1 แถว · แยกชื่อ = เท่าจำนวนผืน (สูงสุด 50) */
export function personRows(pieces: number, mode: NameMode): number {
  return pieces > 1 && mode === 'each' ? Math.min(pieces, MAX_PERSON_ROWS) : 1
}

/** ปรับความยาวรายการชื่อให้เท่าจำนวนแถว (เก็บชื่อที่กรอกไว้แล้ว) */
export function resizePeople(people: MeritPerson[], n: number): MeritPerson[] {
  const out = people.slice(0, n)
  while (out.length < n) out.push({ name: '', birthday: '' })
  return out
}

/** ขั้นกรอกรายละเอียด: ข้อความบนปุ่มเมื่อยังกดไม่ได้ ('' = กดได้) */
export function createBlocker(state: MeritState, amount: number, people: MeritPerson[] | string): string {
  if (state === 'soon') return 'ยังไม่เปิดรับร่วมบุญ'
  if (state !== 'open') return 'ปิดรับร่วมบุญรอบนี้แล้ว'
  if (!(amount > 0)) return 'กรอกจำนวนที่จะร่วมบุญ'
  const list = typeof people === 'string' ? [{ name: people, birthday: '' }] : people
  if (!list.length || !cleanName(list[0].name)) return 'กรอกชื่อผู้ร่วมบุญ'
  const missing = list.findIndex((p) => !cleanName(p.name))
  if (missing > 0) return `กรอกชื่อผืนที่ ${missing + 1}`
  return ''
}

export function buildCreateBody(p: { programId: string; amount: number; people: MeritPerson[]; botId: string; idToken?: string }): Record<string, unknown> {
  const people = p.people.map((x) => ({ name: cleanName(x.name), birthday: String(x.birthday || '').trim().slice(0, 80) })).filter((x) => x.name)
  const first = people[0] || { name: '', birthday: '' }
  return {
    action: 'meritCreate',
    programId: p.programId,
    amount: p.amount,
    donorName: first.name,
    birthday: first.birthday,
    ...(people.length > 1 ? { donors: people } : {}),
    botId: p.botId === '2' ? '2' : '1',
    ...(p.idToken ? { idToken: p.idToken } : {}),
  }
}

/** ชื่อรวมที่ส่งตอนยืนยันชำระ — ต้องตรงกับ donorLabel ฝั่ง CF (ชื่อไม่ซ้ำคั่น ", " · เกิน 80 = "ชื่อแรก และอีก N ท่าน") */
export function peopleLabel(people: MeritPerson[]): string {
  const names = [...new Set(people.map((p) => cleanName(p.name)).filter(Boolean))]
  if (!names.length) return ''
  const all = names.join(', ')
  return all.length <= 80 ? all : `${names[0].slice(0, 50)} และอีก ${names.length - 1} ท่าน`
}

const BD_MONTHS = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']
export const BIRTH_MONTHS = BD_MONTHS
/** วันเกิดจากช่องเลือก → "12 มิถุนายน 2497" · ไม่ครบ = เท่าที่เลือก */
export function birthdayText(d: string, m: string, y: string): string {
  return [d, m ? BD_MONTHS[Number(m) - 1] || '' : '', y].filter(Boolean).join(' ')
}

export function filterDonors(donors: MeritDonor[], q: string): MeritDonor[] {
  const t = String(q || '').trim().toLowerCase()
  return t ? donors.filter((d) => d.name.toLowerCase().includes(t)) : donors
}

const MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
/** YYYY-MM-DD → "5 พ.ย. 2569" · ว่าง/ผิดรูป = '' */
export function thaiDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''))
  if (!m) return ''
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] || ''} ${Number(m[1]) + 543}`
}

/** "5 ต.ค." จากเวลา ISO ของรายการ (เวลาไทย) */
export function thaiShortAt(at: string): string {
  const d = new Date(at)
  if (!at || Number.isNaN(d.getTime())) return ''
  const parts = new Intl.DateTimeFormat('th-TH', { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short' }).format(d)
  return parts
}

/** ข้อความบอกสถานะรอบ (ใต้หัวเรื่อง) */
export function stateNote(p: Pick<MeritProgram, 'state' | 'openAt' | 'closeAt' | 'closedMessage'>): string {
  if (p.state === 'soon') return p.openAt ? `จะเปิดรับวันที่ ${thaiDay(p.openAt)}` : 'ยังไม่เปิดรับร่วมบุญ'
  if (p.state === 'closed') return p.closedMessage || 'ปิดรับร่วมบุญรอบนี้แล้ว ขอบคุณทุกท่านที่ร่วมบุญ'
  return p.closeAt ? `รับร่วมบุญถึง ${thaiDay(p.closeAt)}` : ''
}

/** ชื่อรอบสำหรับป้ายแถบเลือกรอบ: ตัดคำนำ "บุญผ้าดิบ " ถ้ามีเหลืออย่างอื่น */
export function roundChipLabel(name: string): string {
  const s = String(name || '').trim()
  const rest = s.replace(/^บุญผ้าดิบ\s+/, '')
  return rest || s
}

/** แยกคำเน้นสีทองในชื่อรอบ: "บุญผ้าดิบ ต.ค." → ['บุญ','ผ้าดิบ',' ต.ค.'] */
export function splitHeroTitle(name: string): [string, string, string] {
  const s = String(name || '')
  const i = s.indexOf('ผ้าดิบ')
  return i < 0 ? [s, '', ''] : [s.slice(0, i), 'ผ้าดิบ', s.slice(i + 'ผ้าดิบ'.length)]
}
